import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout,
  Person,
  ArrowBackIos,
  ArrowForwardIos
} from '@mui/icons-material';
import { 
  DashboardIcon, 
  CalendarIcon,
  LiveMonitorIcon, 
  SyncManagementIcon, 
  CoursesIcon, 
  GroupsIcon, 
  StudentsIcon, 
  SettingsIcon, 
  UsersIcon 
} from './icons/SvgIcons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import CustomLogo from './CustomLogo';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/', roles: ['admin', 'user'] },
  { text: 'Live Monitor', icon: <LiveMonitorIcon />, path: '/simple-live-monitoring', roles: ['admin', 'user'] },
  { text: 'Calendar', icon: <CalendarIcon />, path: '/calendar', roles: ['admin'] },
  { text: 'Sync Management', icon: <SyncManagementIcon />, path: '/sync', roles: ['admin'] },
  { text: 'Courses', icon: <CoursesIcon />, path: '/courses', roles: ['admin', 'user'] },
  { text: 'Groups', icon: <GroupsIcon />, path: '/groups', roles: ['admin', 'user'] },
  { text: 'Students', icon: <StudentsIcon />, path: '/students', roles: ['admin', 'user'] },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings', roles: ['admin'] },
  { text: 'Users', icon: <UsersIcon />, path: '/users', roles: ['admin'] },
];

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true); // Default closed
  const [currentTime, setCurrentTime] = useState(new Date());
  const { user, logout } = useAuth();

  // Update Athens time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  const drawer = (
    <Box>
      <Toolbar>
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <Box display="flex" alignItems="center" gap={1}>
            <CustomLogo sx={{ color: 'primary.main', fontSize: 32 }} />
            <Box
              sx={{
                overflow: 'hidden',
                transition: 'width 0.3s ease, opacity 0.3s ease',
                width: sidebarCollapsed ? 0 : 'auto',
                opacity: sidebarCollapsed ? 0 : 1,
              }}
            >
              <Typography 
                variant="h6" 
                noWrap 
                component="div" 
                fontWeight="bold"
                sx={{ 
                  whiteSpace: 'nowrap',
                  minWidth: '140px'
                }}
              >
                Zoom Detective
              </Typography>
            </Box>
          </Box>
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {menuItems
          .filter(item => item.roles.includes(user?.role))
          .map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={isActiveRoute(item.path)}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                    },
                  },
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  px: sidebarCollapsed ? 2 : 3,
                  py: 1.5,
                  borderRadius: 2,
                  mx: 1,
                  transition: 'all 0.2s ease',
                }}
              >
                <ListItemIcon sx={{ 
                  color: isActiveRoute(item.path) ? 'primary.main' : 'inherit',
                  minWidth: sidebarCollapsed ? 'unset' : 56,
                  justifyContent: 'center'
                }}>
                  {item.icon}
                </ListItemIcon>
                <Box
                  sx={{
                    overflow: 'hidden',
                    transition: 'width 0.3s ease, opacity 0.2s ease',
                    width: sidebarCollapsed ? 0 : 'auto',
                    opacity: sidebarCollapsed ? 0 : 1,
                    ml: sidebarCollapsed ? 0 : 1,
                  }}
                >
                  <ListItemText 
                    primary={item.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: isActiveRoute(item.path) ? 600 : 400,
                        whiteSpace: 'nowrap',
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Box>
              </ListItemButton>
            </ListItem>
          ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${sidebarCollapsed ? collapsedDrawerWidth : drawerWidth}px)` },
          ml: { sm: `${sidebarCollapsed ? collapsedDrawerWidth : drawerWidth}px` },
          backgroundColor: 'white',
          color: 'black',
          boxShadow: 'none',
          borderBottom: '1px solid #e0e0e0',
          transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Collapsible Sidebar Toggle Button */}
          <IconButton
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            sx={{ 
              mr: 2, 
              display: { xs: 'none', sm: 'flex' },
              width: 36,
              height: 36,
              backgroundColor: 'rgb(255, 255, 255)',
              color: 'black',
              borderRadius: '50%',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgb(240, 240, 240)',
                color: 'black',
                transform: 'scale(1.1)',
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              }
            }}
            size="small"
          >
            {sidebarCollapsed ? <ArrowForwardIos fontSize="small" /> : <ArrowBackIos fontSize="small" />}
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 'Zoom Detective'}
          </Typography>
          
          <Box display="flex" alignItems="center" gap={3}>
            {/* Athens Time */}
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Ώρα Αθήνας: {currentTime.toLocaleTimeString('el-GR', {
                timeZone: 'Europe/Athens',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
            </Typography>
            
            {/* Greeting */}
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Γεια σου {user?.firstname || user?.username}
            </Typography>
            
            {/* User Avatar */}
            <IconButton
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'black', color: 'white' }}>
                {user?.username?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
              transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${sidebarCollapsed ? collapsedDrawerWidth : drawerWidth}px)` },
          mt: 8,
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {children}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {user?.role === 'admin' && (
          <MenuItem onClick={() => handleNavigation('/settings')}>
            <ListItemIcon>
              <SettingsIcon style={{ width: 30, height: 30 }} />
            </ListItemIcon>
            Settings
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => handleNavigation('/profile')}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          Το Προφίλ Μου
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Layout;
